/* eslint-disable no-console */
import { Event, MetricsProvider, StoreProvider } from '@mirai/data-sources';
import { ServiceDictionary, ServiceFeatures } from '@mirai/services';
import React from 'react';
import { createRoot } from 'react-dom/client';

import { EventManager, PerformanceManager } from './contexts';
import { Core } from './Core';
import { COOKIE, EVENT, SYNAPSE_COMMISSION_CODE } from './Core.constants';
import { ErrorBoundary } from './Core.ErrorBoundary';
import {
  checkSession,
  configureErrorTracking,
  consolidateStore,
  fetchConfig,
  fetchCookies,
  fetchExternalSession,
  fetchSession,
  fetchUrlParams,
  findComponents,
  findInstance,
  getLocale,
  initializeErrorTracking,
  loadEnvironment,
  reportError,
  theme,
  trackError,
} from './helpers';
import { name, version } from '../package.json';
import './helpers/polyfills';

window.Mirai = {
  ...window.Mirai,
  components: undefined,
  config: undefined,
  environment: process.env.NODE_ENV,
  Event,
  services: {
    booking: process.env.SERVICE_BOOKING,
    club: process.env.SERVICE_CLUB,
    dictionary: process.env.SERVICE_DICTIONARY,
    engine: process.env.SERVICE_ENGINE,
    static: process.env.SERVICE_STATIC,
    user: process.env.SERVICE_USER,
  },
  trackError,
  version,
};

initializeErrorTracking();

let memoizedRoot;
window.Mirai.core = async () => {
  if (memoizedRoot) memoizedRoot.unmount();

  configureErrorTracking();

  Event.publish(EVENT.CORE_EVENTS_READY);

  const isProduction = process.env.NODE_ENV === 'production';
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isPreproduction = process.env.NODE_ENV === 'preproduction';
  const log = `${isProduction ? '🟢' : isPreproduction ? '🟡' : isDevelopment ? '🔴' : '⚫️'} ${name} v${version}`;

  console.log(log, 'starting...');

  // 1. Find instance
  const urlParams = fetchUrlParams(location.search);
  //Compatibility with 123 compare
  window.Mirai.urlParameters = urlParams;

  const instance = findInstance({ urlParams });
  if (!instance) return;

  configureErrorTracking({
    hotel: { id: instance.type === 'chain' ? instance.hotel : instance.id },
    id: instance.id,
    type: instance.type,
  });

  // 2. Fetch client-side dataSources
  const cookies = fetchCookies(Object.values(COOKIE));
  let session = fetchSession(urlParams);

  // 3. Analyze environment
  const environment = urlParams.env && isProduction ? loadEnvironment(urlParams.env) : undefined;
  if (environment) return;

  // 4. Find components
  const components = findComponents({ ...instance, ...urlParams });

  window.Mirai = { ...window.Mirai, components };

  // 5. Theming
  theme(urlParams);

  const localeInstance = getLocale(instance, urlParams);

  memoizedRoot = createRoot(instance.el);
  // 7. 1st render
  window &&
    !window.IS_PLAYWRIGHT &&
    memoizedRoot.render(
      <React.StrictMode>
        <StoreProvider
          value={{ id: instance.id, session, skeleton: true, type: instance.type, urlParams, ...localeInstance }}
        >
          <Core {...{ components }} />
        </StoreProvider>
      </React.StrictMode>,
    );

  // 8. Analyze id to fetch its config
  const config = await fetchConfig({ ...instance, language: localeInstance.language });
  if (!config) return;
  window.Mirai.config = config;

  // 8. Check session
  if (session && !(await checkSession(instance.id, session, config))) session = undefined;
  if (urlParams.hsri == SYNAPSE_COMMISSION_CODE && urlParams.fingerprint) {
    localStorage?.setItem('MIRAI:FINGERPRINT', urlParams.fingerprint);
  }

  // 9. Fetch external session
  session = await fetchExternalSession({
    config,
    cookies,
    id: instance.id,
    language: localeInstance.language,
    session,
    urlParams,
  });

  // 10. Consolidate store
  const store = await consolidateStore({ components, config, cookies, instance, session, urlParams });
  configureErrorTracking(store);

  // 11. Initialize features
  await ServiceFeatures.initialize();

  // 12. Initialize dictionary
  if (!ServiceDictionary.cache(store.locale)) await ServiceDictionary.get(store.locale, false);

  // 13. Render components
  memoizedRoot.unmount();
  memoizedRoot = createRoot(instance.el);
  memoizedRoot.render(
    <React.StrictMode>
      <ErrorBoundary>
        <MetricsProvider token={!urlParams.disableMetrics ? process.env.METRICS : undefined}>
          <StoreProvider value={store}>
            <PerformanceManager />
            <Core {...{ components }} />
            <EventManager />
          </StoreProvider>
        </MetricsProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );

  // 14. Up & Ready
  window.Mirai.ready = true;
  setTimeout(() => Event.publish(EVENT.CORE_READY, window.Mirai), 0);
  console.log(log, '[READY]', window.Mirai);
};

(async () => {
  try {
    !window.ON_DEMAND_INIT && (await window.Mirai.core());
  } catch (error) {
    reportError(error);

    throw error;
  }

  const { components = [], ready } = window.Mirai;

  if (ready && components.filter(({ component } = {}) => ['booking', 'checkout', 'rates'].includes(component).length)) {
    window.addEventListener('pageshow', (event) => event.persisted && window.Mirai.core());
  }
})();
